import { mutateComponents } from './mutationHelpers';
import { stripeFieldMutator } from './mutators/stripeField';
import { eppoFieldMutator } from './mutators/eppoField';
import { PUBLIC_APP_ENV } from '$env/static/public';
import type { EppoAssignment } from '../eppo';
import type { StripePriceData } from '../stripe';
import { getApiClient } from '$lib/storyblok/sdk';
import type { ISbStoriesParams, ISbStory, ISbStoryParams, StoryblokClient } from '@storyblok/js';
import { stripeShippingMutator } from './mutators/stripeShippingField';
import type { Stripe } from 'stripe';

export interface MutatorCache {
	eppo: Record<string, EppoAssignment>;
	stripe: Record<string, StripePriceData>;
	shipping: Record<string, Stripe.ShippingRate>;
}

export interface PromiseCache {
	eppo: Record<string, Promise<EppoAssignment>>;
	stripe: Record<string, Promise<StripePriceData>>;
	shipping: Record<string, Promise<Stripe.ShippingRate>>;
}

const VERSION = PUBLIC_APP_ENV === 'prod' ? 'published' : 'draft';

let client: StoryblokClient;

export type SbApiClient = ReturnType<typeof getSbApiClient>;

const DEFAULT_RESOLVE_RELATIONS = ['page_filter.allow_list', 'page_filter.block_list'];

export function getSbApiClient(fetchHandler: typeof fetch, url: URL) {
	if (!client) {
		client = getApiClient();
	}

	const cache: MutatorCache = { eppo: {}, stripe: {}, shipping: {} };
	const promiseCache: PromiseCache = { eppo: {}, stripe: {}, shipping: {} };

	const mutators = [
		eppoFieldMutator(fetchHandler, url, cache.eppo, promiseCache.eppo),
		stripeFieldMutator(fetchHandler, cache.stripe, promiseCache.stripe),
		stripeShippingMutator(fetchHandler, cache.shipping, promiseCache.shipping)
	];

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const getStory = async <Content = any>(
		slug: string,
		{ resolve_relations = [], ...params }: ISbStoryParams = {}
	) => {
		const result = await client.getStory(slug, {
			version: VERSION,
			resolve_relations: [
				...DEFAULT_RESOLVE_RELATIONS,
				...(Array.isArray(resolve_relations) ? resolve_relations : [resolve_relations])
			],
			...params
		});

		await Promise.all([
			mutateComponents(result.data.story.content, mutators),
			...result.data.rels.map((rel) => mutateComponents(rel.content, mutators))
		]);

		return result as ISbStory<Content>;
	};

	const getStories: typeof client.getStories = async ({ resolve_relations = [], ...params }) => {
		const result = await client.getStories({
			version: VERSION,
			resolve_relations: [
				...DEFAULT_RESOLVE_RELATIONS,
				...(Array.isArray(resolve_relations) ? resolve_relations : [resolve_relations])
			],
			...params
		});

		await Promise.all([
			...result.data.stories.map((story) => mutateComponents(story.content, mutators)),
			...result.data.rels.map((rel) => mutateComponents(rel.content, mutators))
		]);

		return result;
	};

	const get = async (slug: string, params: ISbStoriesParams = {}) => {
		return await client.get(slug, { version: VERSION, ...params });
	};

	const getAll = async (slug: string, params: ISbStoriesParams = {}) => {
		return await client.getAll(slug, { version: VERSION, ...params });
	};

	const flushCache: typeof client.flushCache = async () => {
		return await client.flushCache();
	};

	return {
		getStory,
		getStories,
		get,
		getAll,
		flushCache
	};
}
